/**
 * /dashboard/posh — POSH module Overview (Phase 3d — 2026-06-01).
 *
 * The module is a Pattern E sub-app — the rail in PoshModuleShell
 * handles cross-section navigation (Overview / Training / Complaints /
 * ICC / Policy / Annual reports). This Overview page surfaces the same
 * destinations in tile form for users landing on /dashboard/posh from
 * the main sidebar.
 *
 * Per-page role gates remain at each destination + the backend; tiles
 * for HR-admin / ICC-only destinations are filtered out here too.
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/posh/page.tsx flattened with
 * the old `layout.tsx` into one route that uses PoshModuleShell.
 *   - next/link → @tanstack/react-router Link (typed for the ones we
 *     have routes for; plain <a> for the certificates/compliance tiles
 *     since those routes register in this same Phase 3d batch).
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Award,
  ClipboardCheck,
  FileBarChart,
  FileText,
  GraduationCap,
  MessageSquareWarning,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/types/auth";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { PageHeader } from "@/components/shared/page-header";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/")({
  component: PoshOverviewRoute,
});

const HR_ADMINS: Role[] = ["company_admin", "hr_manager"];
const ICC_ROLES: Role[] = [
  "company_admin",
  "hr_manager",
  "icc_member",
];

interface PoshTile {
  label: string;
  href: string;
  icon: LucideIcon;
  blurb: string;
  roles?: Role[];
}

const TILES: PoshTile[] = [
  {
    label: "Training",
    href: "/app/$tenantSlug/posh/training",
    icon: GraduationCap,
    blurb:
      "Interactive POSH scenarios and module quizzes. Earn a certificate on each pass.",
  },
  {
    label: "My Certificates",
    href: "/app/$tenantSlug/posh/certificates",
    icon: Award,
    blurb:
      "Download the certificates you have earned and share a public verification link.",
  },
  {
    label: "Complaints",
    href: "/app/$tenantSlug/posh/complaints",
    icon: MessageSquareWarning,
    blurb:
      "File and track a POSH complaint. The committee register is protected by two-factor authentication.",
  },
  {
    label: "ICC",
    href: "/app/$tenantSlug/posh/icc",
    icon: UsersRound,
    blurb:
      "Internal Complaints Committee composition (POSH Act s.4). Requires two-factor authentication.",
    roles: ICC_ROLES,
  },
  {
    label: "Policy",
    href: "/app/$tenantSlug/posh/policy",
    icon: FileText,
    blurb:
      "Generate, edit and publish your organisation’s POSH policy with immutable version history.",
    roles: HR_ADMINS,
  },
  {
    label: "Compliance",
    href: "/app/$tenantSlug/posh/compliance",
    icon: ClipboardCheck,
    blurb: "Org-wide training and certification compliance roster.",
    roles: HR_ADMINS,
  },
  {
    label: "Annual Reports",
    href: "/app/$tenantSlug/posh/reports",
    icon: FileBarChart,
    blurb:
      "Statutory counts-only annual report (POSH Act s.21) — generate, file and download the PDF.",
    roles: HR_ADMINS,
  },
];

function PoshOverviewRoute() {
  const { user } = useAuth();
  const role = user?.role;
  const tiles = TILES.filter(
    (t) => !t.roles || (role && t.roles.includes(role)),
  );

  return (
    <PoshModuleShell>
      <PoshFeatureGate>
        <PageHeader
          title="Overview"
          description="The POSH module at a glance."
        />
        <div className="grid gap-4 sm:grid-cols-2">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.href}
                to={t.href}
                className="group"
              >
                <Card className="h-full transition-colors group-hover:border-primary/40">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                        <Icon className="h-5 w-5" />
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                    </div>
                    <CardTitle className="mt-3 text-base">
                      {t.label}
                    </CardTitle>
                    <CardDescription>{t.blurb}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
